import React from "react";
import { ActivityIcon, AlertTriangleIcon, ZapIcon, BoxIcon, LineChartIcon, ShieldCheckIcon, DollarSignIcon, TargetIcon, UsersIcon, RocketIcon, FileKeyIcon, BookOpenIcon, GaugeIcon } from "lucide-react";
export type Metric = {
  value: string;
  label: string;
  href?: string;
};
export type Mechanism = {
  title: string;
  body: string;
};
export type ComparisonRow = {
  pain: string;
  answer: string;
};
export type CompetitiveTableRow = {
  approach: string;
  approachDetail: string;
  decodeEfficiency: string;
  decodeDetail?: string;
  deterministicLatency: string;
  deterministicDetail?: string;
  modelChange: string;
  modelChangeDetail?: string;
};
export type MoatStage = {
  number: string;
  title: string;
  detail: string;
};
export type VelocityCard = {
  value: string;
  detail: React.ReactNode;
  className?: string;
};
export type VelocityChip = {
  prefix: string;
  suffix?: string;
};
export type PositioningPoint = {
  name: string;
  description: string;
  x: number;
  y: number;
  featured?: boolean;
};
export type Slide = {
  id: string;
  section: string;
  icon: BoxIcon;
  title: React.ReactNode;
  lead?: string;
  body: string[];
  portrait?: {
    src: string;
    alt: string;
  };
  mechanisms?: Mechanism[];
  comparisonRows?: ComparisonRow[];
  competitiveTableRows?: CompetitiveTableRow[];
  moatStages?: MoatStage[];
  moatStats?: Metric[];
  moatHeadline?: React.ReactNode;
  moatDescription?: React.ReactNode;
  moatConclusion?: React.ReactNode;
  velocityHeadline?: React.ReactNode;
  velocityDescription?: string;
  velocityCards?: VelocityCard[];
  velocityChips?: VelocityChip[];
  positioningPoints?: PositioningPoint[];
  efficiencyPoints?: string[];
  proof?: string;
  footer?: string;
  metrics?: Metric[];
  kind: 'title' | 'standard' | 'business' | 'metrics' | 'defensibility' | 'appendix' | 'competition' | 'comparison-table' | 'moat' | 'velocity' | 'positioning-map';
};
// Highlight helper that renders an accent-colored word within a title.
const Accent = ({
  children
}: {
  children: React.ReactNode;
}) => <span className="text-accent" data-id="element-64" data-mp="16gep6w9qewv8">{children}</span>;
export const slides: Slide[] = [{
  id: 'title',
  section: 'OpenFPGA',
  icon: ActivityIcon,
  kind: 'title',
  title: <>
        <span className="block lg:whitespace-nowrap" data-id="element-65" data-mp="1ke3ag57po60t">We don't make voice agents.</span>
        <Accent data-id="element-66" data-mp="1gf3zo3zatf1g"><span className="block lg:whitespace-nowrap" data-id="element-67" data-mp="1gfdlrgh8321v">We make them better.</span></Accent>
      </>,
  body: []
}, {
  id: 'problem',
  section: 'Problem',
  icon: AlertTriangleIcon,
  kind: 'standard',
  title: <><span className="block" data-id="element-68" data-mp="nntztot8r9w6">GPUs can't guarantee latency.</span><Accent data-id="element-69" data-mp="11id64bcb2xjr"><span className="block" data-id="element-70" data-mp="11i4t8jg6ns8k">Frozen silicon can't run new models.</span></Accent></>,
  lead: 'Users feel p99, not average latency.',
  body: ['Voice products break when latency spikes, turns lag, and interruptions stop feeling natural.', 'GPU stacks are built for throughput, not deterministic conversational response at concurrency.']
}, {
  id: 'solution',
  section: 'Solution',
  icon: ZapIcon,
  kind: 'standard',
  title: <><span className="block" data-id="element-71" data-mp="3qzlc12awkwc7">Voice AI inference that runs</span><Accent data-id="element-72" data-mp="3vruvq0lfd061"><span className="block" data-id="element-73" data-mp="3vso6dcni89tg">on time.</span></Accent></>,
  lead: 'OpenFPGA serves fixed-state models with deterministic scheduling and no queueing tail.',
  body: ['Our TTFT stays flat across turns while transformer systems re-prefill and slow down.']
}, {
  id: 'product',
  section: 'Product',
  icon: BoxIcon,
  kind: 'standard',
  title: <>
        Built for <Accent data-id="element-74" data-mp="il2wygmjogj3">Real-Time Voice</Accent>
      </>,
  lead: 'We sell stable live inference, not raw peak tokens.',
  body: ['We multiplex many live sessions on one FPGA while preserving batch-1 responsiveness.', 'The system hot-swaps specialist models in 113 ms, so one box can serve different voice and chat workloads.']
}, {
  id: 'traction',
  section: 'Product Traction',
  icon: LineChartIcon,
  kind: 'metrics',
  title: <>
        Proof, Not <Accent data-id="element-75" data-mp="2te74g1nraf4s">Promise</Accent>
      </>,
  lead: 'Key achievements in silicon at the 30-day mark.',
  body: ['A 1.4B ternary Mamba is live on real FPGA silicon. The flat-TTFT serving mode is live, hot-swap is proven, and mux economics are validated.'],
  metrics: [{
    value: '1.4B',
    label: 'Ternary Mamba live on silicon'
  }, {
    value: '18.57',
    label: 'Tokens / sec sustained'
  }, {
    value: '21 µs',
    label: 'p99-p50 jitter'
  }, {
    value: '113 ms',
    label: 'Model hot-swap'
  }]
}, {
  id: 'defensibility',
  section: 'Defensibility',
  icon: FileKeyIcon,
  kind: 'defensibility',
  title: <>
        The Moat Is the <Accent data-id="element-76" data-mp="3nhyw27zs0c2x">Serving System</Accent>
      </>,
  lead: 'Models are swappable commodities. Our durable advantage is the deterministic FPGA serving layer we have built and are actively protecting.',
  body: ['Patent pending. Two provisional applications filed in 2026 cover time-multiplexing a single FPGA across concurrent inference streams with hard per-stream latency guarantees.', 'We switch a small, fixed recurrent state as an atomic, constant-cost operation. That is a fundamentally cheaper primitive than switching entire workloads.'],
  mechanisms: [{
    title: 'Admission control',
    body: 'Accept streams up to a provable capacity bound.'
  }, {
    title: 'Predictive state management',
    body: 'Keep the right users resident on-chip.'
  }, {
    title: 'Cross-user compute sharing',
    body: 'Amortize memory bandwidth across live streams.'
  }]
}, {
  id: 'why-we-win',
  section: 'Why We Win',
  icon: ShieldCheckIcon,
  kind: 'standard',
  title: <>Concurrency is the <Accent data-id="element-77" data-mp="15co348yo2c8u">Product.</Accent></>,
  lead: 'We sell streams at a guaranteed SLA, not best-effort tokens.',
  body: ["A GPU box's capacity is a guess that degrades as users are added. Ours is a number: N streams per card, every one in lockstep.", 'OpenFPGA is designed around fixed-cycle serving, fixed-state models, and concurrency as the product.']
}, {
  id: 'business-model',
  section: 'Business Model',
  icon: DollarSignIcon,
  kind: 'business',
  title: <>
        Sell Minutes, Sell <Accent data-id="element-78" data-mp="393vgx3tolqg5">Tokens</Accent>
      </>,
  lead: 'The same system supports both, but voice-first pricing keeps the wedge aligned with real-time UX.',
  body: ['We price voice infrastructure the way customers experience value: per minute, per live session, or by concurrency tier.', 'We also expose the same infrastructure as token-factory capacity in dollars per million tokens for broader API buyers.']
}, {
  id: 'market',
  section: 'Market',
  icon: TargetIcon,
  kind: 'standard',
  title: <>
        Start With <Accent data-id="element-79" data-mp="3nzuugtzk8iso">Voice Infrastructure</Accent>
      </>,
  lead: 'One infrastructure customer can distribute OpenFPGA across thousands of downstream agents.',
  body: ['The beachhead is voice AI platforms and infrastructure providers that already feel bad UX under load.', 'From there, we expand into other latency-critical power-limited real-time venues for AI such as avionics and automotive.']
}, {
  id: 'team',
  section: 'Founder-Market Fit',
  icon: UsersIcon,
  kind: 'standard',
  title: <>The Right Founder</>,
  lead: 'Hardware, systems, and AI are not adjacent domains here. They are the work.',
  body: ['Orion Parrott is a YC S16 alum and founding GP of Orange DAO, the community of YC-backed founders.', 'With BSEE, MSEE, and MBA training, early neural-network studies and experience at Raytheon, and a patent for a portable scalable signal processor, he brings the rare combination of hardware and systems depth this problem requires.'],
  portrait: {
    src: 'https://cdn.magicpatterns.com/uploads/unJc7B4Nnj2osh2QT56F6h/260507_Orion_Bio_Photo_Web.jpg',
    alt: 'Orion Parrott, founder of OpenFPGA'
  }
}, {
  id: 'ask',
  section: 'The Ask',
  icon: RocketIcon,
  kind: 'metrics',
  title: <>Raising <Accent data-id="element-80" data-mp="6hxgqib1b8mc">$1M</Accent></>,
  lead: 'The long-term vision is the serving substrate for real-time AI.',
  body: ['OpenFPGA is raising $1M to upgrade hardware, prove integrated multi-stream silicon, and land first paying voice customers. We are scaling a proven wedge: deterministic UX under concurrency.'],
  metrics: [{
    value: '$1M',
    label: 'Seed round'
  }, {
    value: 'Hardware',
    label: 'Upgrade & scale'
  }, {
    value: 'Multi-stream',
    label: 'Integrated silicon'
  }, {
    value: 'First customers',
    label: 'Paying voice platforms'
  }]
}, {
  id: 'appendix',
  section: 'Appendix',
  icon: BookOpenIcon,
  kind: 'appendix',
  title: <>Appendix</>,
  body: [],
  metrics: [{
    value: 'Orion Parrott',
    label: 'Founder, OpenFPGA',
    href: 'https://parro.tt'
  }, {
    value: 'orion@openfpga.ai',
    label: 'Email',
    href: 'mailto:orion@openfpga.ai'
  }, {
    value: 'linkedin.com/in/orionparrott',
    label: 'LinkedIn',
    href: 'https://linkedin.com/in/orionparrott'
  }, {
    value: 'openfpga.ai',
    label: 'Company',
    href: 'https://openfpga.ai'
  }]
}, {
  id: 'competition',
  section: 'Competitive Positioning',
  icon: GaugeIcon,
  kind: 'competition',
  title: <>Competitive positioning</>,
  body: [],
  competitiveTableRows: [{
    approach: 'GPU',
    approachDetail: 'general-purpose',
    decodeEfficiency: 'Low',
    decodeDetail: 'bandwidth-bound, jitters under load',
    deterministicLatency: 'No',
    modelChange: 'Instant',
    modelChangeDetail: 'software'
  }, {
    approach: 'Groq LPU',
    approachDetail: 'compiler-scheduled',
    decodeEfficiency: 'High',
    deterministicLatency: 'Yes',
    modelChange: 'Weeks',
    modelChangeDetail: 'recompile the dataflow'
  }, {
    approach: 'Cerebras',
    approachDetail: 'wafer-scale',
    decodeEfficiency: 'High',
    decodeDetail: 'at scale',
    deterministicLatency: 'Mostly',
    modelChange: 'Slow'
  }, {
    approach: 'Taalas',
    approachDetail: 'model baked into an ASIC',
    decodeEfficiency: 'Highest',
    deterministicLatency: 'Yes',
    modelChange: 'Months + $millions',
    modelChangeDetail: 'silicon tape-out per model'
  }, {
    approach: 'OpenFPGA',
    approachDetail: 'reconfigurable silicon',
    decodeEfficiency: 'High',
    decodeDetail: 'ternary, multiply-free, 7-8 W',
    deterministicLatency: 'Yes',
    deterministicDetail: 'jitter-free by construction',
    modelChange: 'Hours',
    modelChangeDetail: 'reconfigure, no tape-out'
  }],
  proof: 'Proven on AWS F2 (Virtex UltraScale+): 1.4B ternary SSM, bit-exact on silicon, 7-8 W measured'
}, {
  id: 'competitive-positioning-map',
  section: 'Competitive Landscape',
  icon: GaugeIcon,
  kind: 'positioning-map',
  title: <><span className="block" data-id="element-81" data-mp="8925jyxhf8tk">Deterministic and reconfigurable.</span><Accent data-id="element-82" data-mp="89ahnols6a3p"><span className="block" data-id="element-83" data-mp="86y0verre5s8">Until now you had to pick.</span></Accent></>,
  body: [],
  positioningPoints: [{
    name: 'Taalas',
    description: 'model etched forever',
    x: 8,
    y: 13
  }, {
    name: 'Groq LPU (NVIDIA $20B)',
    description: 'lockstep per config, agile across configs\ncloud only, rack scale',
    x: 26,
    y: 22
  }, {
    name: 'd-Matrix',
    description: 'fixed in-memory silicon',
    x: 22,
    y: 42
  }, {
    name: 'Cerebras',
    description: 'fast, not guaranteed',
    x: 32,
    y: 70
  }, {
    name: 'GPU clouds',
    description: 'flexible, jittery tail',
    x: 83,
    y: 87
  }, {
    name: 'OpenFPGA',
    description: 'open quadrant',
    x: 79,
    y: 18,
    featured: true
  }]
}, {
  id: 'moat',
  section: 'Appendix',
  icon: FileKeyIcon,
  kind: 'moat',
  title: <>The moat</>,
  body: [],
  moatHeadline: <>The model and the silicon are <span className="text-accent" data-id="element-84" data-mp="2u9ddfizf2895">trained together.</span></>,
  moatDescription: <>We don't quantize off-the-shelf models. We co-design and train specialized ternary SSMs<br className="hidden lg:block" data-id="element-85" data-mp="2khhb1vqarvz4" /> for our silicon, and spin up a new one in hours. Any single model is swappable, the moat is<br className="hidden lg:block" data-id="element-86" data-mp="2gi8mr6e10qay" /> the <em data-id="element-87" data-mp="2bhkuyihz45fo">pipeline</em> that puts it on deterministic silicon.</>,
  moatStages: [{
    number: '01',
    title: 'DISTILL',
    detail: 'From a strong teacher\nspecialize a small SSM to the task'
  }, {
    number: '02',
    title: 'TERNARY-QAT',
    detail: '1.58-bit, our datapath\nmultiply-free, trained for the fabric'
  }, {
    number: '03',
    title: 'PACK',
    detail: 'Into the weight layout\nthe exact format the engine reads'
  }, {
    number: '04',
    title: 'DROP-IN',
    detail: 'Onto the live AFI\nbit-exact, no bitstream rebuild'
  }],
  moatStats: [{
    value: '3',
    label: 'voice specialists (12L / 16L / 24L) hot-swappable on one bitstream'
  }, {
    value: '19',
    label: 'models across 4 families'
  }, {
    value: 'ternary quality',
    label: 'narrows with scale · 350M → 1.4B'
  }, {
    value: 'new specialist → on silicon in',
    label: 'hours'
  }]
}, {
  id: 'velocity',
  section: 'Appendix',
  icon: LineChartIcon,
  kind: 'velocity',
  title: <>30-Day Velocity</>,
  body: [],
  velocityHeadline: <>Proof, Not Promise</>,
  velocityDescription: 'A small team, one month. Every number below is on real FPGA silicon or a live surface, not a projection.',
  velocityCards: [{
    value: '18.57 tok/s',
    detail: <><strong data-id="element-88" data-mp="runxdf0ip09d">1.4B ternary Mamba, bit-exact on real FPGA silicon</strong> · 7 W · p99-p50 jitter 0.026%</>
  }, {
    value: '~hours',
    detail: <><strong data-id="element-89" data-mp="mv1i7014p9xx">idea → new model running on silicon</strong> · pipeline turnaround</>
  }, {
    value: '19',
    detail: <><strong data-id="element-90" data-mp="wmxlqqlzhs9j">models trained</strong> · 3 voice specialists hot-swappable on one AFI, no rebuild</>
  }, {
    value: 'Live',
    detail: <><strong data-id="element-91" data-mp="8y61clemye7b">app.openfpga.ai</strong> · base + voice model-switching + live-power telemetry</>
  }],
  velocityChips: [{
    prefix: '+20% clock',
    suffix: ' · 187.5 → 225 MHz, silicon-validated'
  }, {
    prefix: '8-bit concurrency substrate',
    suffix: ' · math + silicon plumbing validated'
  }, {
    prefix: '1 signed design-partner LOI',
    suffix: ' · ego.live'
  }, {
    prefix: '2 provisional patents',
    suffix: ' filed'
  }]
}];